import React from 'react';
import styled, { css } from 'styled-components';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { focusRing } from '@/app/components/search/styles/mixins';
import { EvoSection } from '@/app/components/hotel-detail/evo/EvoCommon';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { useTranslation } from '@/app/components/translations/Translation';
import { Text } from '@lmn/design-system';
import { Icon } from '@/app/common/icons/Icon';

type EvoBreadcrumbsProps = React.HTMLAttributes<HTMLElement> & {
  activeStep: number;
  /** Called when a completed step is clicked. Completed steps become links only when provided. */
  onStepClick?: (stepNumber: number) => void;
};

const STEP_CHOOSE_STAY = 'vacation.select.cc.nav.step1';
const STEP_CHOOSE_FLIGHT = 'vacation.select.cc.nav.step2';
const STEP_CHOOSE_TRAIN = 'vacation.select.cc.nav.step2.train';
const STEP_ADD_EXTRAS = 'vacation.select.cc.nav.step3';
const STEP_REVIEW_AND_PAY = 'vacation.select.cc.nav.step4';

/**
 * The steps of the EVO funnel, in order, and the single source of both the numbering and the count —
 * every consumer takes a step's number from its position here, and EvoSubNavigation's progress bar
 * reads the length.
 *
 * Step 2 is the transport step, so it varies twice over: it says "Train" on T+H (MPH-934), and Hotel
 * Only drops it altogether, leaving three steps — 1 Choose Stay · 2 Add Extras · 3 Review and Pay
 * (MPH-935). The renumbering is automatic because the numbers come from the positions.
 */
export function useStepKeys() {
  const { accommodationOnly, isTrain } = useUrlSearchParams();
  if (accommodationOnly) return [STEP_CHOOSE_STAY, STEP_ADD_EXTRAS, STEP_REVIEW_AND_PAY];
  return [STEP_CHOOSE_STAY, isTrain ? STEP_CHOOSE_TRAIN : STEP_CHOOSE_FLIGHT, STEP_ADD_EXTRAS, STEP_REVIEW_AND_PAY];
}

const Container = styled(EvoSection)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border-bottom: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderDisabled};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media ${customCss.breakpoints.sm} {
    padding: ${({ theme }) => theme.kallisto.space.space100} ${({ theme }) => theme.kallisto.space.space200};
    overflow-x: visible;
  }
`;

const StepCircle = styled.div<{ $active: boolean; $completed: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${(p) =>
    p.$completed
      ? p.theme.kallisto.color.background.static.backgroundNeutralContrast
      : p.$active
        ? p.theme.kallisto.color.background.static.backgroundLayer02
        : p.theme.kallisto.color.background.static.backgroundLayer01};
  border: ${(p) =>
    p.$active
      ? `2px solid ${p.theme.kallisto.color.border.interactive.borderNeutralDefault}`
      : p.$completed
        ? 'none'
        : `1px solid ${p.theme.kallisto.color.border.static.borderDisabled}`};
`;

const StepNumber = styled(Text).attrs({ variant: 'BodySmall', weight: 'bold' })<{ $active: boolean }>`
  color: ${(p) =>
    p.$active ? p.theme.kallisto.color.text.static.textPrimary : p.theme.kallisto.color.text.static.textSecondary};
`;

const StepLabel = styled(Text).attrs({ variant: 'BodySmall', weight: 'medium' })<{ $active: boolean; $link?: boolean }>`
  white-space: nowrap;
  color: ${(p) =>
    p.$active || p.$link
      ? p.theme.kallisto.color.text.static.textPrimary
      : p.theme.kallisto.color.text.static.textSecondary};
  ${(p) =>
    p.$link &&
    css`
      text-decoration: underline;
    `}
`;

const StepItem = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding: ${({ theme }) => theme.kallisto.space.space50} 0;

  ${(p) =>
    p.$clickable &&
    css`
      appearance: none;
      margin: 0;
      background: none;
      border: none;
      border-radius: ${p.theme.kallisto.space.space50};
      cursor: pointer;
      font: inherit;

      &:focus-visible {
        ${focusRing}
      }
    `}
`;

export function CheckMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M12.8618 3.52864C13.1222 3.26829 13.5442 3.26829 13.8045 3.52864C14.0649 3.78899 14.0649 4.21099 13.8045 4.47134L6.4712 11.8047C6.21085 12.065 5.78884 12.065 5.52849 11.8047L2.19516 8.47134L2.14958 8.42056C1.93602 8.15871 1.95108 7.77271 2.19516 7.52864C2.43923 7.28456 2.82523 7.2695 3.08708 7.48306L3.13786 7.52864L5.99984 10.3906L12.8618 3.52864Z"
        fill="white"
      />
    </svg>
  );
}

export function EvoBreadcrumbs({ activeStep, onStepClick, ...props }: EvoBreadcrumbsProps) {
  const { t } = useTranslation();
  const stepKeys = useStepKeys();

  return (
    <Container {...props}>
      {stepKeys.map((stepKey, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === activeStep;
        const isCompleted = stepNumber < activeStep;
        const isClickable = isCompleted && !!onStepClick;
        return (
          <React.Fragment key={stepKey}>
            {index > 0 && (
              <Icon category="Arrows" name="icon-chevron-right" size={10} footprint={16} color="iconSecondary" alt="" />
            )}
            {isClickable ? (
              <StepItem as="button" type="button" $clickable={true} onClick={() => onStepClick(stepNumber)}>
                <StepCircle $active={isActive} $completed={isCompleted}>
                  {isCompleted ? <CheckMark /> : <StepNumber $active={isActive}>{stepNumber}</StepNumber>}
                </StepCircle>
                <StepLabel $active={isActive} $link={isClickable}>
                  {t(stepKey)}
                </StepLabel>
              </StepItem>
            ) : (
              <StepItem $clickable={false}>
                <StepCircle $active={isActive} $completed={isCompleted}>
                  {isCompleted ? <CheckMark /> : <StepNumber $active={isActive}>{stepNumber}</StepNumber>}
                </StepCircle>
                <StepLabel $active={isActive} $link={isClickable}>
                  {t(stepKey)}
                </StepLabel>
              </StepItem>
            )}
          </React.Fragment>
        );
      })}
    </Container>
  );
}
